<!--
  TeamPenaltiesDisplay.vue

  Groups and displays hand penalties by team, showing each player's
  remaining cards as visual card fans with their individual penalties.

  Used during the round-end penalties phase to provide a breakdown
  of cards caught in hand for each team member.
-->

<template>
  <div class="team-penalties-display" :class="`team-${team.toLowerCase()}`">
    <!-- Players List -->
    <div class="players-grid">
      <PlayerHandPenalty
        v-for="(player, index) in teamPlayers"
        :key="player.id"
        :player-id="player.id"
        :player-name="player.username"
        :display-name="player.display_name || player.username"
        :is-bot="player.is_bot"
        :hand="player.hand || []"
        :foot="player.foot || []"
        :penalty-amount="getPlayerPenalty(player)"
        :is-animating="isAnimating"
        :animation-delay="index * 500"
        @animation-complete="onPlayerAnimationComplete(index)"
      />
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import PlayerHandPenalty from './PlayerHandPenalty.vue'
import { useCards } from '@/composables/useCards'

// ============================================================================
// Types
// ============================================================================

interface CardData {
  rank: string
  suit: string
}

interface PlayerHandData {
  id: string
  username: string
  display_name?: string
  team: 'A' | 'B'
  seat: number
  is_bot: boolean
  hand: CardData[]
  foot: CardData[]
}

interface Props {
  team: 'A' | 'B'
  teamName: string
  playerHands: PlayerHandData[]
  totalPenalty: number
  opponentTotalPenalty?: number
  isAnimating?: boolean
}

// ============================================================================
// Props & Emits
// ============================================================================

const props = withDefaults(defineProps<Props>(), {
  opponentTotalPenalty: 0,
  isAnimating: false,
})

const emit = defineEmits<{
  animationComplete: []
}>()

// ============================================================================
// State
// ============================================================================

const completedAnimations = ref(0)
const showSummary = ref(false)

// ============================================================================
// Composables
// ============================================================================

const { getCardPoints } = useCards()

// ============================================================================
// Computed
// ============================================================================

/**
 * Filter players to just this team
 */
const teamPlayers = computed(() => {
  return props.playerHands.filter(p => p.team === props.team)
})

// ============================================================================
// Methods
// ============================================================================

/**
 * Calculate penalty for a single player's hand
 * Note: Red 3s are handled separately in PenaltyDisplay, so we exclude them here
 * and use absolute values since having cards in hand is always a penalty
 */
function getPlayerPenalty(player: PlayerHandData): number {
  let penalty = 0

  // Calculate penalty from hand cards
  const allCards = [...(player.hand || []), ...(player.foot || [])]

  for (const card of allCards) {
    // Skip red 3s - they're penalized separately in the Red 3s section
    const rank = String(card.rank || '').toUpperCase()
    const suit = String(card.suit || '').toLowerCase()
    if (rank === '3' && (suit === 'hearts' || suit === 'diamonds')) {
      continue
    }

    // Use absolute value since all cards in hand count against you
    penalty += Math.abs(getCardPoints(card))
  }

  return penalty
}

/**
 * Handle individual player animation completion
 */
function onPlayerAnimationComplete(index: number): void {
  completedAnimations.value++

  // Show summary when all players have animated
  if (completedAnimations.value >= teamPlayers.value.length) {
    showSummary.value = true

    // Emit completion after summary appears
    setTimeout(() => {
      emit('animationComplete')
    }, 400)
  }
}

/**
 * Delay helper
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// ============================================================================
// Lifecycle
// ============================================================================

onMounted(() => {
  if (!props.isAnimating) {
    // Show everything immediately
    completedAnimations.value = teamPlayers.value.length
    showSummary.value = true
  }
})

watch(() => props.isAnimating, (animating) => {
  if (animating) {
    completedAnimations.value = 0
    showSummary.value = false
  }
}, { immediate: true })

// ============================================================================
// Expose
// ============================================================================

defineExpose({
  getPlayerPenalty,
})
</script>

<style scoped>
.team-penalties-display {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
}

/* Players List - vertical stacking */
.players-grid {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

</style>
